/**
 * Keep start/end times and activity duration minutes aligned.
 * Mirrors iOS ScheduleDurationSync (ActivityDurationFormatting.swift).
 */

import { hmFromUnknown } from './wallClock';

export const DEFAULT_SPAN_SECONDS = 3600;
export const DEFAULT_SPAN_MINUTES = 60;

function hmToMinutes(hm: string): number | null {
  const parsed = hmFromUnknown(hm);
  if (!parsed) return null;
  const [h, m] = parsed.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function minutesToHm(totalMinutes: number): string {
  const wrapped = ((totalMinutes % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Duration in whole minutes from end − start (floored at 0; same-day only). */
export function durationMinutesFromStartEnd(startHm: string, endHm: string): number {
  const start = hmToMinutes(startHm);
  const end = hmToMinutes(endHm);
  if (start == null || end == null) return 0;
  return Math.max(0, end - start);
}

/** Duration in whole seconds for metadata persistence. */
export function durationSecondsFromStartEnd(startHm: string, endHm: string): number {
  return durationMinutesFromStartEnd(startHm, endHm) * 60;
}

/** Prior schedule span in minutes; falls back to 60 if invalid. */
export function preservedSpanMinutes(startHm: string, endHm: string): number {
  const span = durationMinutesFromStartEnd(startHm, endHm);
  return span > 0 ? span : DEFAULT_SPAN_MINUTES;
}

/** End HM = start + duration minutes (non-negative, wraps within day). */
export function endHmFromStartAndDuration(
  startHm: string,
  durationMinutes: number
): string {
  const start = hmToMinutes(startHm);
  if (start == null) return startHm;
  const minutes = Math.max(0, durationMinutes);
  return minutesToHm(start + minutes);
}
